'use client'

import { IUser, SignInResponse } from '@/api/auth/entities'
import { ScreenLoader } from '@/app/components/molecules/ScreenLoader'
import usePersistentState from '@/hooks/usePersistentState'
import axios from 'axios'
import Keycloak, { KeycloakOnLoad } from 'keycloak-js'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import { IAuthState } from './types'

declare global {
  interface Window {
    tokenRefreshInterval?: number
    newApp?: any
  }
}

let keycloakInstance: Keycloak | null = null

const initOptions = {
  url: process.env.NEXT_PUBLIC_KEYCLOAK_URL as string,
  realm: process.env.NEXT_PUBLIC_KEYCLOAK_REALM as string,
  clientId: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID as string,
  onLoad: 'login-required' as KeycloakOnLoad
}

const AuthContext = createContext<IAuthState>({
  user: undefined,
  setUser: () => {},
  token: undefined,
  setToken: () => {},
  error: null,
  setError: () => {},
  verifiedUser: undefined,
  signIn: () => ({ success: false, user: {} as any }),
  signOut: () => {},
  isLoggedIn: () => false,
  hasRole: () => false,
})

export const AuthProvider: React.FC<React.PropsWithChildren & { useKeycloak?: boolean }> = ({
  children,
  useKeycloak = true
}) => {
  const [initialized, setInitialized] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const [user, setUser] = usePersistentState<IUser | undefined>(
    '@keycloak.user',
    undefined
  )
  const [token, setTokenState] = usePersistentState<string | undefined>(
    '@keycloak.token',
    undefined
  )
  const [error, setError] = useState<string | null>(null)
  const [verifiedUser, setVerifiedUser] = useState<IUser | null | undefined>(
    undefined
  )

  const setAuthToken = useCallback(
    (token: string) => {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('authorization', token)
      }
      setTokenState(token)
    },
    [setTokenState]
  )

  const isLoggedIn = useCallback(() => {
    return (
      !!keycloakInstance?.authenticated &&
      !!user &&
      (user.sessionExpiresIn ? user.sessionExpiresIn > Date.now() : true)
    )
  }, [user])

  const parseUserFromKeycloak = useCallback(async () => {
    if (!keycloakInstance) return undefined

    const keycloakToken = keycloakInstance.token
    const keycloakTokenParsed = keycloakInstance.tokenParsed || {}

    return await axios
      .get(`${process.env.NEXT_PUBLIC_API_URL}/get-user-info`, {
        headers: {
          Authorization: `Bearer ${keycloakInstance?.token}`
        }
      })
      .then(({ data }) => {
        return {
          user_id: data.userID || '',
          name:
            keycloakTokenParsed.name ||
            keycloakTokenParsed.preferred_username ||
            '',
          email: keycloakTokenParsed.email || '',
          access_token: keycloakToken || '',
          role: data.roleName,
          account_id: data.clientID,
          sessionExpiresIn: keycloakTokenParsed.exp
            ? keycloakTokenParsed.exp * 1000
            : undefined
        }
      })
      .catch(() => {
        keycloakInstance?.logout()
        setUser(undefined)
        setTokenState(undefined)

        return undefined
      })
  }, [setUser, setTokenState])

  const signIn = (): SignInResponse => {
    try {
      if (!keycloakInstance) {
        throw new Error('Authentication service not initialized')
      }

      keycloakInstance.login()

      return { success: true }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to login'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }
  }

  const hasRole = useCallback(
    (roleName: string) => {
      return !!user?.role?.toLowerCase().includes(roleName.toLowerCase())
    },
    [user]
  )

  const signOut = useCallback(() => {
    if (keycloakInstance) {
      keycloakInstance.logout()
    }

    setUser(undefined)
    setVerifiedUser(null)
    setAuthToken('')
  }, [setAuthToken, setUser])

  const setupTokenRefresh = useCallback(() => {
    if (!keycloakInstance) return () => {}

    const callback = function () {
      keycloakInstance
        ?.updateToken(70)
        .then(async (refreshed) => {
          if (refreshed && keycloakInstance?.token) {
            setAuthToken(keycloakInstance.token)

            if (keycloakInstance.tokenParsed) {
              const updatedUser = await parseUserFromKeycloak()
              if (updatedUser) {
                setUser(updatedUser)
              }
            }
          }
        })
        .catch(() => {
          if (keycloakInstance) {
            keycloakInstance.clearToken()
          }
          signOut()
        })
    }

    callback()

    window.tokenRefreshInterval = window.setInterval(callback, 1750000)

    return () => {
      if (window.tokenRefreshInterval) {
        window.clearInterval(window.tokenRefreshInterval)
        delete window.tokenRefreshInterval
      }
    }
  }, [parseUserFromKeycloak, setAuthToken, setUser, signOut])

  useEffect(() => {
    if (initialized || keycloakInstance) {
      return
    }
    if (!useKeycloak) {
      setLoading(false)
      return
    }

    const initKeycloak = async () => {
      try {
        setLoading(true)

        keycloakInstance = new Keycloak(initOptions)

        const authenticated = await keycloakInstance.init({
          onLoad: initOptions.onLoad,
          checkLoginIframe: false,
          pkceMethod: 'S256',
          responseMode: 'query',
        })

        setInitialized(true)

        if (authenticated) {
          const mappedUser = await parseUserFromKeycloak()

          if (mappedUser) {
            setUser(mappedUser)
            setVerifiedUser(mappedUser)
            setAuthToken(keycloakInstance?.token || '')

            setupTokenRefresh()
          }
        } else {
          setVerifiedUser(null)

          keycloakInstance.login()
        }
      } catch {
        setInitialized(true)
        setVerifiedUser(null)

        if (keycloakInstance) {
          keycloakInstance.login()
        }
      } finally {
        setLoading(false)
      }
    }

    initKeycloak()

    return () => {
      if (window.tokenRefreshInterval) {
        window.clearInterval(window.tokenRefreshInterval)
        delete window.tokenRefreshInterval
      }
    }
  }, [
    initialized,
    parseUserFromKeycloak,
    setAuthToken,
    setUser,
    setupTokenRefresh
  ])

  const parseAuthResponse = useCallback((usercontext: IUser) => {
    if (window.newApp) {
      window.newApp.userData = {
        accessToken: usercontext.access_token,
        roles: usercontext.role
      }
    }
  }, [])

  useEffect(() => {
    if(!useKeycloak) return
    if (user) {
      parseAuthResponse(user)
    }
  }, [user, parseAuthResponse])

  if (loading) {
    return <ScreenLoader />
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        setToken: setAuthToken,
        error,
        setError,
        verifiedUser,
        signIn,
        signOut,
        isLoggedIn,
        hasRole
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
